/* eslint-disable max-len */
import { SelectOutlined } from '@ant-design/icons';
import { Alert, Button, Tabs } from 'antd';
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { ROUTES } from '../../const/routes';
import { useActiveSchoolId } from '../../hooks/useActiveSchoolId';
import { useSocketMessage } from '../../hooks/useSocketMessage';
import { useSystemStore } from '../../stores/SystemStore';
import { trpc } from '../../utils/trcp';
import CarLoader from '../CarLoader';
import Center from '../Center';
import EntityTagManager from '../Settings/EntityTagManager';
import StudentGroup from '../StudentGroup';
import StudentState from '../StudentState';
import HoverableEditItem from './HoverableEditItem';
import StatsSection from './StatsSection';
import { useTabs } from './Tabs/useTabs';

const StudentName = styled.span`
	/* padding-bottom: 1em; */
	font-size: 26px !important;
	font-style: normal !important;
	font-weight: 400 !important;
	line-height: normal !important;
	/* height: 100% !important; */
`;

const NameWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	margin-top: 0.5rem;

	div {
		display: flex;
		flex-direction: row;
		align-items: center;
	}
`;

const ContactWrapper = styled.div`
	display: flex;
	flex-flow: row wrap;
	align-items: center;
	margin: 1rem 0;
	gap: 5px;
`;

const StudentGroupWrapper = styled.div`
	padding: 0 10px;
	background-color: #f0f0f0;
	border-radius: 1rem;
	margin-right: 0.5rem;
`;

export enum StudentCardTabs {
	INFO = '1',
	TEACHING = '2',
	TRAINING = '3',
	PAYMENT = '4',
}

interface Props {
	id: string;
	showLink?: boolean;
}

const NewStudentCard: React.FC<Props> = ({ id, showLink }) => {
	const [searchParams, setSearchParams] = useSearchParams({ activeTab: StudentCardTabs.INFO });
	const { activeSchoolId } = useActiveSchoolId();
	const setUserCardId = useSystemStore(state => state.setUserCardId);
	const getStudentCardHeaderInfo = trpc.student.getStudentCardHeaderInfo.useQuery({
		id,
		schoolId: activeSchoolId,
	});

	const student = getStudentCardHeaderInfo.data;
	const activeTab = searchParams.get('activeTab') ?? StudentCardTabs.INFO;

	const tabs = useTabs(id);

	const handleChangeTab = (key: string) => {
		setSearchParams({ activeTab: key });
	};

	useSocketMessage(
		['TeachingLessonRecordMessage', 'StudentInfoMessage', 'PaymentItemMessage', 'DrivingLessonRecordMessage'],
		d => {
			if (d.studentId === id) {
				getStudentCardHeaderInfo.refetch();
			}
		},
	);

	if (getStudentCardHeaderInfo.isLoading) {
		return (
			<Center hasPadding>
				<CarLoader />
			</Center>
		);
	}

	if (!student) {
		return <Alert message="Student nebyl nalezen" type="error" showIcon />;
	}

	return (
		<div data-testid="student-card">
			<NameWrapper>
				<div>
					<HoverableEditItem
						form={{
							property: ['firstName', 'lastName'],
							value: {
								firstName: student.studentInfo.firstName,
								lastName: student.studentInfo.lastName,
							},
							type: 'studentInfo',
							studentId: id,
							schoolId: activeSchoolId,
							onDone: getStudentCardHeaderInfo.refetch,
							border: false,
						}}
					>
						<StudentName>{`${student.studentInfo!.firstName} ${student.studentInfo!.lastName}`}</StudentName>
					</HoverableEditItem>
					<div>
						<StudentState state={student.state} />
					</div>
					<div style={{ marginTop: 8 }}>
						<EntityTagManager entityId={id} entityType="STUDENT" />
					</div>
				</div>
				{showLink && (
					<Link
						to={{
							pathname: `${ROUTES.APP.STUDENT_CARD}/${id}`,
							search: `?activeTab=${activeTab}`,
						}}
					>
						<Button icon={<SelectOutlined />} type="link" onClick={() => setUserCardId(undefined)} />
					</Link>
				)}
			</NameWrapper>
			<ContactWrapper>
				<StudentGroupWrapper>
					{student.studentStudyInfo!.group.map((g, i) => (
						<React.Fragment key={g}>
							<StudentGroup type={g} />
							{i !== student.studentStudyInfo!.group.length - 1 && ', '}
						</React.Fragment>
					))}
				</StudentGroupWrapper>
				<HoverableEditItem
					valueUndefined={!student.studentInfo.phone}
					form={{
						property: 'phone',
						value: student.studentInfo.phone,
						type: 'studentInfo',
						studentId: id,
						schoolId: activeSchoolId,
						onDone: getStudentCardHeaderInfo.refetch,
						border: false,
					}}
				>
					<>{student.studentInfo.phone ?? 'Telefon není vyplněn'}</>
				</HoverableEditItem>
				<HoverableEditItem
					valueUndefined={!student.studentInfo.email}
					form={{
						property: 'email',
						value: student.studentInfo.email,
						type: 'studentInfo',
						studentId: id,
						schoolId: activeSchoolId,
						onDone: getStudentCardHeaderInfo.refetch,
						border: false,
					}}
				>
					<>{student.studentInfo.email ?? 'E-mail není vyplněn'}</>
				</HoverableEditItem>
			</ContactWrapper>
			<StatsSection
				training={student.training}
				trainingTeaching={student.trainingTeaching}
				teaching={student.teaching}
				payment={student.payment}
			/>
			<Tabs data-testid="student-card-tabs" activeKey={activeTab} onChange={handleChangeTab} items={tabs} />
		</div>
	);
};

export default NewStudentCard;
